import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowLeft, ArrowRight, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { AuthenticatedImage } from './AuthenticatedImage';
import { PhoneHQButton } from 'ch3-ui-lib';
import { ImageInfo } from './chatTypes';
import { getMatrixMediaBlob } from '../../utils/matrixMedia';

const HORIZONTAL_PADDING = 32;

interface ImageGalleryProps {
    images: ImageInfo[];
    initialIndex?: number;
    onClose: () => void;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
    images,
    initialIndex = 0,
    onClose
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isImageZoomed, setIsImageZoomed] = useState(false);

    const handlePrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
        setIsImageZoomed(false);
    }, [images.length]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
        setIsImageZoomed(false);
    }, [images.length]);

    const handleDownload = useCallback(async () => {
        const image = images[currentIndex];
        try {
            const blob = await getMatrixMediaBlob(image.url, image.encryptedFile, image.mimeType);
            const url = URL.createObjectURL(blob);
            try {
                const a = document.createElement('a');
                a.href = url;
                a.download = image.alt || 'image';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } finally {
                URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error('Failed to download image:', err);
        }
    }, [images, currentIndex]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') handlePrevious();
            if (e.key === 'ArrowRight') handleNext();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, handlePrevious, handleNext]);

    return (
        <div
            className="fixed inset-0 flex flex-col z-50 backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.84)' }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            {/* Header */}
            <div className="absolute m-1 top-0 left-0 right-0 p-4 flex items-center justify-end gap-2 z-10 ">
                <PhoneHQButton
                    variant="secondary"
                    size="icon"
                    onClick={handleDownload}
                    className="!w-9 !h-9 flex items-center justify-center"
                    aria-label="Download image"
                >
                    <Download size={20} />
                </PhoneHQButton>
                <PhoneHQButton
                    variant="secondary"
                    size="icon"
                    onClick={() => setIsImageZoomed(prev => !prev)}
                    className="!w-9 !h-9 flex items-center justify-center"
                    aria-label={isImageZoomed ? 'Zoom out image' : 'Zoom in image'}
                >
                    {isImageZoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
                </PhoneHQButton>
                <PhoneHQButton
                    variant="secondary"
                    size="icon"
                    onClick={onClose}
                    className="!w-9 !h-9 flex items-center justify-center"
                    aria-label="Close gallery"
                >
                    <X size={20} />
                </PhoneHQButton>
            </div>

            {/* Main Image */}
            <div className="flex-1 flex items-start justify-center p-4 pt-20 pb-32 overflow-auto">
                <div className={`flex ${isImageZoomed ? 'items-start' : 'items-center'} justify-center min-h-full`}>
                    <div
                        className="relative cursor-pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsImageZoomed(prev => !prev);
                        }}
                    >
                        <AuthenticatedImage
                            key={`gallery-image-${currentIndex}`}
                            src={images[currentIndex].url}
                            alt={images[currentIndex].alt}
                            encryptedFile={images[currentIndex].encryptedFile}
                            mimeType={images[currentIndex].mimeType}
                            thumbnailSrc={images[currentIndex].thumbnailUrl}
                            thumbnailEncryptedFile={images[currentIndex].thumbnailEncryptedFile}
                            thumbnailWidth={images[currentIndex].thumbnailWidth}
                            thumbnailHeight={images[currentIndex].thumbnailHeight}
                            className={`object-contain ${
                                isImageZoomed
                                    ? 'max-w-none cursor-zoom-out'
                                    : 'max-w-full max-h-[calc(100vh-250px)] cursor-zoom-in'
                            }`}
                            style={{
                                width: isImageZoomed ? 'min(1200px, calc(100vw - 32px))' : undefined,
                                maxWidth: isImageZoomed ? 'none' : `calc(100vw - ${HORIZONTAL_PADDING}px)`
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Navigation Arrows */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={handlePrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-ink-disabled transition-colors p-2 rounded-full"
                        aria-label="Previous image"
                    >
                        <ArrowLeft className="p-1.5" color={'white'} size={32} />
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-ink-disabled transition-colors p-2  rounded-full"
                        aria-label="Next image"
                    >
                        <ArrowRight className="p-1.5" color={'white'} size={32} />
                    </button>
                </>
            )}

            {/* Thumbnail Navigation */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex gap-2 justify-center overflow-x-auto max-w-full pb-2">
                    {images.map((image, index) => (
                        <button
                            key={`${image.url}-${index}`}
                            onClick={() => setCurrentIndex(index)}
                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                                index === currentIndex
                                    ? 'border-white scale-110'
                                    : 'border-transparent opacity-60 hover:opacity-100'
                            }`}
                        >
                            <AuthenticatedImage
                                src={image.url}
                                alt={image.alt}
                                encryptedFile={image.encryptedFile}
                                mimeType={image.mimeType}
                                thumbnailSrc={image.thumbnailUrl}
                                thumbnailEncryptedFile={image.thumbnailEncryptedFile}
                                thumbnailWidth={image.thumbnailWidth}
                                thumbnailHeight={image.thumbnailHeight}
                                className="w-full h-full object-cover"
                            />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
